import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { NumberInput, Select, TextInput } from '@mantine/core';
import { useForm, type UseFormReturnType } from '@mantine/form';
import { z } from 'zod';
import { CLUSTER_TYPES } from '@seo/contracts';
import { useTRPC } from '../lib/trpc';
import { RowEditor } from '../components/RowEditor';
import { CreatePanel } from '../components/CreatePanel';
import { zodResolver } from '../lib/zod-resolver';

// `GameEditor` живе в `./GameEditor` (картка гри виросла до ~20 полів і власного файлу);
// ре-експорт тут лишає імпорт `from './EntityEditor'` робочим для наявних споживачів.
export { GameEditor, type EditableGame } from './GameEditor';

/**
 * `NumberInput` віддає `number | ''` — порожнє поле це рядок, а не `null`.
 *
 * Патч розрізняє «не чіпати» (`undefined`) і «прибрати» (`null`), тож порожній рядок мусить
 * стати саме `null`: інакше стерти рік заснування можна було б лише вписавши нуль.
 */
function optionalNumber(value: number | string): number | null {
  return typeof value === 'number' ? value : null;
}

/**
 * Картки трьох сутностей-власників сайтів: бренд, кластер, гра.
 *
 * У всіх трьох реєстрах `update` існував в API з першого дня і не мав жодного викликача з
 * інтерфейсу, а `delete` для бренду й кластера не існував узагалі — його додано 14.08 разом
 * із гардом «на кого посилаються». Це екран до обох половин.
 *
 * Три окремі компоненти, а не один із вибором неймспейсу: `patch` у трьох процедур різний, і
 * `useMutation` на юніоні не збігається сам із собою. Спільне — `RowEditor` і форма.
 */

export interface EditableBrand {
  id: string;
  name: string;
  foundedYear: number | null;
  platform: string | null;
  holding: string | null;
  originalUrl: string | null;
  signupUrl?: string | null;
  originalLanguage?: string | null;
  licenseId?: string | null;
  licenseNumber?: string | null;
  registryUrl?: string | null;
  legalEntityFooter?: string | null;
}

interface BrandFormValues {
  name: string;
  foundedYear: number | '';
  platform: string;
  holding: string;
  originalUrl: string;
  signupUrl: string;
  originalLanguage: string;
  licenseId: string;
  licenseNumber: string;
  registryUrl: string;
  legalEntityFooter: string;
}

const emptyBrandFormValues: BrandFormValues = {
  name: '',
  foundedYear: '',
  platform: '',
  holding: '',
  originalUrl: '',
  signupUrl: '',
  originalLanguage: '',
  licenseId: '',
  licenseNumber: '',
  registryUrl: '',
  legalEntityFooter: '',
};

function brandFormValuesOf(row: EditableBrand): BrandFormValues {
  return {
    name: row.name,
    foundedYear: row.foundedYear ?? '',
    platform: row.platform ?? '',
    holding: row.holding ?? '',
    originalUrl: row.originalUrl ?? '',
    signupUrl: row.signupUrl ?? '',
    originalLanguage: row.originalLanguage ?? '',
    licenseId: row.licenseId ?? '',
    licenseNumber: row.licenseNumber ?? '',
    registryUrl: row.registryUrl ?? '',
    legalEntityFooter: row.legalEntityFooter ?? '',
  };
}

const optionalUrlField = z.string().superRefine((raw, ctx) => {
  const value = raw.trim();
  if (value === '' || z.url().safeParse(value).success) return;
  ctx.addIssue('Некоректний URL');
});

/** `brandInput` (apps/api/src/routers/brands.ts) вимагає 2–3 символи, якщо код узагалі заданий. */
const optionalLanguageField = z.string().superRefine((raw, ctx) => {
  const len = raw.trim().length;
  if (len === 0 || (len >= 2 && len <= 3)) return;
  ctx.addIssue('Код мови — 2 або 3 символи, напр. fr');
});

/**
 * Одна схема на створення й редагування бренду.
 *
 * До 14.08 панель створення мала єдине поле (назву), а картка редагування не показувала шість
 * полів, які `brandInput` приймає з першого дня — `signupUrl`, `originalLanguage`, `licenseId`,
 * `licenseNumber`, `registryUrl`, `legalEntityFooter`. Дві окремі схеми розійшлися б протягом
 * тижня: поле, додане в `brandInput`, лишалося б в одній формі й губилось в іншій — точнісінько
 * той дефект, що вже стався зі складом бренду (`bonuses.remove`, `setGeos` — див.
 * `packages/core/src/services/brands.ts`). Тому обидва режими діляться однією схемою.
 */
const brandFormSchema = z.object({
  name: z.string().trim().min(1, 'Вкажіть назву бренда').max(200),
  foundedYear: z.union([z.number(), z.literal('')]),
  platform: z.string().max(120),
  holding: z.string().max(200),
  originalUrl: optionalUrlField,
  signupUrl: optionalUrlField,
  originalLanguage: optionalLanguageField,
  licenseId: z.string(),
  licenseNumber: z.string().max(120),
  registryUrl: optionalUrlField,
  legalEntityFooter: z.string().max(500),
});

/** Форма до дроту: порожній рядок ⇒ `null`, так само, як в інших картках реєстру. */
function brandPatchOf(values: BrandFormValues): {
  name: string;
  foundedYear: number | null;
  platform: string | null;
  holding: string | null;
  originalUrl: string | null;
  signupUrl: string | null;
  originalLanguage: string | null;
  licenseId: string | null;
  licenseNumber: string | null;
  registryUrl: string | null;
  legalEntityFooter: string | null;
} {
  const text = (v: string): string | null => (v.trim() === '' ? null : v.trim());
  return {
    name: values.name.trim(),
    foundedYear: optionalNumber(values.foundedYear),
    platform: text(values.platform),
    holding: text(values.holding),
    originalUrl: text(values.originalUrl),
    signupUrl: text(values.signupUrl),
    originalLanguage: text(values.originalLanguage),
    licenseId: values.licenseId || null,
    licenseNumber: text(values.licenseNumber),
    registryUrl: text(values.registryUrl),
    legalEntityFooter: text(values.legalEntityFooter),
  };
}

/**
 * Одинадцять полів, спільних для панелі створення й картки редагування.
 *
 * Рендериться з обох місць `BrandEditor` нижче — це і є «спільність за побудовою»: поле, яке
 * забули дописати сюди, відсутнє одразу в обох режимах, а не лише в одному непомітно.
 */
function BrandFields({
  form,
  prefix,
  licenseOptions,
  licensesPending,
}: {
  form: UseFormReturnType<BrandFormValues>;
  prefix: string;
  licenseOptions: { value: string; label: string }[];
  licensesPending: boolean;
}): React.JSX.Element {
  return (
    <>
      <TextInput
        size="xs"
        label="Назва"
        data-testid={`${prefix}-name`}
        style={{ width: 180 }}
        {...form.getInputProps('name')}
      />
      <NumberInput
        size="xs"
        label="Рік"
        min={1900}
        max={2100}
        data-testid={`${prefix}-founded`}
        style={{ width: 90 }}
        {...form.getInputProps('foundedYear')}
      />
      <TextInput
        size="xs"
        label="Платформа"
        data-testid={`${prefix}-platform`}
        style={{ width: 140 }}
        {...form.getInputProps('platform')}
      />
      <TextInput
        size="xs"
        label="Холдинг"
        data-testid={`${prefix}-holding`}
        style={{ width: 140 }}
        {...form.getInputProps('holding')}
      />
      <TextInput
        size="xs"
        label="Сайт бренду"
        data-testid={`${prefix}-url`}
        style={{ width: 200 }}
        {...form.getInputProps('originalUrl')}
      />
      <TextInput
        size="xs"
        label="URL реєстрації"
        data-testid={`${prefix}-signup-url`}
        style={{ width: 200 }}
        {...form.getInputProps('signupUrl')}
      />
      <TextInput
        size="xs"
        label="Мова оригіналу"
        placeholder="fr"
        data-testid={`${prefix}-language`}
        style={{ width: 90 }}
        {...form.getInputProps('originalLanguage')}
      />
      <Select
        size="xs"
        label="Ліцензія"
        placeholder={licensesPending ? 'завантаження…' : 'не обрано'}
        data={licenseOptions}
        clearable
        searchable
        data-testid={`${prefix}-license`}
        style={{ width: 180 }}
        {...form.getInputProps('licenseId')}
      />
      <TextInput
        size="xs"
        label="Номер ліцензії"
        data-testid={`${prefix}-license-number`}
        style={{ width: 150 }}
        {...form.getInputProps('licenseNumber')}
      />
      <TextInput
        size="xs"
        label="URL реєстру"
        data-testid={`${prefix}-registry-url`}
        style={{ width: 200 }}
        {...form.getInputProps('registryUrl')}
      />
      <TextInput
        size="xs"
        label="Юрособа у футері"
        data-testid={`${prefix}-legal-footer`}
        style={{ width: 220 }}
        {...form.getInputProps('legalEntityFooter')}
      />
    </>
  );
}

export type BrandEditorProps =
  | {
      mode: 'create';
      open: boolean;
      onOpenChange: (open: boolean) => void;
      /** Оператор має продовжити просто у склад щойно створеного бренду, а не впасти назад у список. */
      onCreated: (id: string) => void;
    }
  | {
      mode: 'edit';
      row: EditableBrand;
      onClose: () => void;
    };

/**
 * Один компонент на створення й редагування бренду.
 *
 * `mode` вибирає лише обгортку (`CreatePanel` проти `RowEditor`) і мутацію (`create` проти
 * `update`) — сам перелік полів дає `BrandFields`, спільний для обох викликів нижче. Друга,
 * рукописна форма поруч із цією розійшлася б протягом тижня (див. коментар над
 * `brandFormSchema`), тому її тут немає.
 */
export function BrandEditor(props: BrandEditorProps): React.JSX.Element {
  const trpc = useTRPC();
  const queryClient = useQueryClient();
  const [confirming, setConfirming] = useState(false);

  // Довідник ліцензій — той самий список, що на екрані довідників і в картці ГЕО
  // (`GeoPresetEditor`), а не копія в межах бренду.
  const licenses = useQuery(
    trpc.reference.licenses.list.queryOptions({ pagination: { limit: 200, direction: 'asc' } }),
  );
  const licenseOptions = (licenses.data?.items ?? []).map((l: { id: string; name: string }) => ({
    value: l.id,
    label: l.name,
  }));

  const form = useForm<BrandFormValues>({
    initialValues: props.mode === 'edit' ? brandFormValuesOf(props.row) : emptyBrandFormValues,
    validate: zodResolver(brandFormSchema),
  });

  const refresh = async (): Promise<void> => {
    await queryClient.invalidateQueries({ queryKey: trpc.brands.pathKey() });
  };

  const create = useMutation(
    trpc.brands.create.mutationOptions({
      onSuccess: async (result) => {
        form.reset();
        await refresh();
        if (props.mode === 'create') {
          props.onOpenChange(false);
          props.onCreated(result.id);
        }
      },
    }),
  );
  const update = useMutation(trpc.brands.update.mutationOptions({ onSuccess: refresh }));
  const remove = useMutation(
    trpc.brands.delete.mutationOptions({
      onSuccess: async () => {
        await refresh();
        if (props.mode === 'edit') props.onClose();
      },
    }),
  );

  if (props.mode === 'create') {
    return (
      <CreatePanel
        onReset={() => {
          create.reset();
          form.reset();
        }}
        zone="databases"
        label="Додати бренд"
        testId="brand"
        open={props.open}
        onOpenChange={props.onOpenChange}
        busy={create.isPending}
        error={create.error}
        onSubmit={form.onSubmit((values) => {
          create.mutate(brandPatchOf(values));
        })}
      >
        <BrandFields
          form={form}
          prefix="brand"
          licenseOptions={licenseOptions}
          licensesPending={licenses.isPending}
        />
      </CreatePanel>
    );
  }

  const { row, onClose } = props;

  return (
    <RowEditor
      title={row.name}
      testId="brand"
      zone="databases"
      onClose={onClose}
      busy={update.isPending}
      error={update.error}
      deleting={remove.isPending}
      deleteError={remove.error}
      confirmingDelete={confirming}
      onConfirmingDeleteChange={setConfirming}
      deletePrompt={`Видалити бренд «${row.name}»? Разом із ним зникнуть його бонуси, кольори, ассети й привʼязки до ГЕО.`}
      onDelete={() => {
        remove.mutate({ id: row.id });
      }}
      onSubmit={form.onSubmit((values) => {
        update.mutate({ id: row.id, patch: brandPatchOf(values) });
      })}
    >
      <BrandFields
        form={form}
        prefix="edit-brand"
        licenseOptions={licenseOptions}
        licensesPending={licenses.isPending}
      />
    </RowEditor>
  );
}

export interface EditableCluster {
  id: string;
  name: string;
  type: string;
  language: string;
  sitesPlanned: number;
}

export function ClusterEditor({
  row,
  onClose,
}: {
  row: EditableCluster;
  onClose: () => void;
}): React.JSX.Element {
  const trpc = useTRPC();
  const queryClient = useQueryClient();
  const [confirming, setConfirming] = useState(false);

  const refresh = async (): Promise<void> => {
    await queryClient.invalidateQueries({ queryKey: trpc.clusters.pathKey() });
  };
  const update = useMutation(trpc.clusters.update.mutationOptions({ onSuccess: refresh }));
  const remove = useMutation(
    trpc.clusters.delete.mutationOptions({
      onSuccess: async () => {
        await refresh();
        onClose();
      },
    }),
  );

  const form = useForm({
    initialValues: { name: row.name, type: row.type, sitesPlanned: row.sitesPlanned },
  });

  return (
    <RowEditor
      title={row.name}
      testId="cluster"
      zone="databases"
      onClose={onClose}
      busy={update.isPending}
      error={update.error}
      deleting={remove.isPending}
      deleteError={remove.error}
      confirmingDelete={confirming}
      onConfirmingDeleteChange={setConfirming}
      deletePrompt={`Видалити кластер «${row.name}»? Разом із ним зникнуть його підкластери й офери.`}
      onDelete={() => {
        remove.mutate({ id: row.id });
      }}
      onSubmit={form.onSubmit((values) => {
        update.mutate({
          id: row.id,
          patch: {
            name: values.name,
            type: values.type as (typeof CLUSTER_TYPES)[number],
            sitesPlanned: values.sitesPlanned,
          },
        });
      })}
    >
      <TextInput
        size="xs"
        label="Назва"
        data-testid="edit-cluster-name"
        style={{ width: 200 }}
        {...form.getInputProps('name')}
      />
      <Select
        size="xs"
        label="Тип"
        allowDeselect={false}
        data={[...CLUSTER_TYPES]}
        data-testid="edit-cluster-type"
        style={{ width: 140 }}
        {...form.getInputProps('type')}
      />
      <NumberInput
        size="xs"
        label="Сайтів у плані"
        min={0}
        data-testid="edit-cluster-sites-planned"
        style={{ width: 120 }}
        {...form.getInputProps('sitesPlanned')}
      />
    </RowEditor>
  );
}

